import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { RootLayout } from '@/components/layout/RootLayout';
import { NotFoundPage } from '@/app/NotFoundPage';
import { RequireSession } from '@/app/RequireSession';
import { ADMIN_NAV_GROUP } from '@/app/navigation';
import type { AppRouteHandle } from '@/lib/routeHandle';

/**
 * Route table for the app.
 *
 * Every page is `lazy()`-loaded so a route ships its own chunk — adding a
 * feature must not grow the initial bundle. Register new routes here; features
 * export their page from their own `index.ts` barrel.
 */
const WelcomePage = lazy(() =>
    import('@/features/welcome').then((m) => ({ default: m.WelcomePage }))
);
const SignInPage = lazy(() =>
    import('@/features/auth').then((m) => ({ default: m.SignInPage }))
);
const ConsentPage = lazy(() =>
    import('@/features/consent').then((m) => ({ default: m.ConsentPage }))
);
const RequireConsentRequest = lazy(() =>
    import('@/features/consent').then((m) => ({
        default: m.RequireConsentRequest,
    }))
);
const SignedInHeader = lazy(() =>
    import('@/app/header/SignedInHeader').then((m) => ({
        default: m.SignedInHeader,
    }))
);
const AdminArea = lazy(() =>
    import('@/features/admin').then((m) => ({ default: m.AdminArea }))
);
const AdminUsersWorkspace = lazy(() =>
    import('@/features/admin').then((m) => ({
        default: m.AdminUsersWorkspace,
    }))
);
const AdminSpotsWorkspace = lazy(() =>
    import('@/features/admin').then((m) => ({
        default: m.AdminSpotsWorkspace,
    }))
);
const AdminSubscriptionsWorkspace = lazy(() =>
    import('@/features/admin').then((m) => ({
        default: m.AdminSubscriptionsWorkspace,
    }))
);
const AdminChallengesWorkspace = lazy(() =>
    import('@/features/admin').then((m) => ({
        default: m.AdminChallengesWorkspace,
    }))
);
const AdminApiClientsWorkspace = lazy(() =>
    import('@/features/admin').then((m) => ({
        default: m.AdminApiClientsWorkspace,
    }))
);
const DeveloperPortal = lazy(() =>
    import('@/features/developer').then((m) => ({
        default: m.DeveloperPortal,
    }))
);

export const routes: RouteObject[] = [
    {
        path: '/',
        element: <RootLayout signedInHeader={<SignedInHeader />} />,
        children: [
            { index: true, element: <WelcomePage /> },
            { path: 'login', element: <SignInPage /> },
            {
                path: 'authorize',
                // 1. Is there a request id at all? Must run before the
                // session check below, or AC8 breaks — see RequireConsentRequest.
                element: <RequireConsentRequest />,
                children: [
                    {
                        // 2. Pathless guard — is there a session?
                        element: <RequireSession />,
                        children: [{ index: true, element: <ConsentPage /> }],
                    },
                ],
            },
            {
                path: 'admin',
                children: [
                    {
                        index: true,
                        element: <Navigate to="/admin/users" replace />,
                    },
                    {
                        // 1. Signed in? Must wrap the access gate, or a
                        // signed-out visit would send the access check.
                        element: <RequireSession />,
                        children: [
                            {
                                // 2. Access gate + outlet. The section nav is the site
                                // header; the gate reads the same list.
                                element: (
                                    <AdminArea
                                        sections={ADMIN_NAV_GROUP.items}
                                    />
                                ),
                                children: [
                                    {
                                        path: 'users',
                                        element: <AdminUsersWorkspace />,
                                        handle: {
                                            layout: 'workspace',
                                        } satisfies AppRouteHandle,
                                        // Element-less children: they give each
                                        // drill-down step a URL and a handle. The
                                        // workspace renders every pane itself, so
                                        // the List never unmounts.
                                        children: [
                                            {
                                                path: ':userId',
                                                handle: {
                                                    pane: 'user',
                                                } satisfies AppRouteHandle,
                                                children: [
                                                    {
                                                        path: 'sessions',
                                                        handle: {
                                                            pane: 'sessions',
                                                        } satisfies AppRouteHandle,
                                                        children: [
                                                            {
                                                                path: ':sessionId',
                                                                handle: {
                                                                    pane: 'session',
                                                                } satisfies AppRouteHandle,
                                                            },
                                                        ],
                                                    },
                                                    {
                                                        path: 'transactions',
                                                        handle: {
                                                            pane: 'transactions',
                                                        } satisfies AppRouteHandle,
                                                    },
                                                    {
                                                        path: 'partners',
                                                        handle: {
                                                            pane: 'partners',
                                                        } satisfies AppRouteHandle,
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: 'spots',
                                        element: <AdminSpotsWorkspace />,
                                        handle: {
                                            layout: 'workspace',
                                        } satisfies AppRouteHandle,
                                        // Element-less child: a URL and a
                                        // handle for the open spot. The
                                        // workspace renders both panes, so
                                        // the List never unmounts.
                                        children: [
                                            {
                                                path: ':spotId',
                                                handle: {
                                                    pane: 'spot',
                                                } satisfies AppRouteHandle,
                                            },
                                        ],
                                    },
                                    {
                                        path: 'subscriptions',
                                        element: (
                                            <AdminSubscriptionsWorkspace />
                                        ),
                                        handle: {
                                            layout: 'workspace',
                                        } satisfies AppRouteHandle,
                                        // Element-less child: a URL and a
                                        // handle for the open lookup. The
                                        // workspace renders both columns, so
                                        // the lookup never unmounts.
                                        children: [
                                            {
                                                path: ':transactionId',
                                                handle: {
                                                    pane: 'subscription',
                                                } satisfies AppRouteHandle,
                                            },
                                        ],
                                    },
                                    {
                                        path: 'challenges',
                                        element: <AdminChallengesWorkspace />,
                                        handle: {
                                            layout: 'workspace',
                                        } satisfies AppRouteHandle,
                                        // Element-less children: a URL and a
                                        // handle for the open pane. The
                                        // workspace renders the list and the
                                        // pane itself, so the list never
                                        // unmounts. `new` outranks
                                        // `:challengeId`.
                                        children: [
                                            {
                                                path: 'new',
                                                handle: {
                                                    pane: 'create',
                                                } satisfies AppRouteHandle,
                                            },
                                            {
                                                path: ':challengeId',
                                                handle: {
                                                    pane: 'challenge',
                                                } satisfies AppRouteHandle,
                                            },
                                        ],
                                    },
                                    {
                                        path: 'api-clients',
                                        element: <AdminApiClientsWorkspace />,
                                        handle: {
                                            layout: 'workspace',
                                        } satisfies AppRouteHandle,
                                        // Element-less children: a URL and a
                                        // handle for the open pane. The
                                        // workspace renders the list and the
                                        // pane itself, so the list never
                                        // unmounts. `new` outranks `:clientId`.
                                        children: [
                                            {
                                                path: 'new',
                                                handle: {
                                                    pane: 'create',
                                                } satisfies AppRouteHandle,
                                            },
                                            {
                                                path: ':clientId',
                                                handle: {
                                                    pane: 'client',
                                                } satisfies AppRouteHandle,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    { path: '*', element: <NotFoundPage /> },
                ],
            },
            {
                path: 'developer',
                // Signed in? Nothing else: any account may open the portal (an
                // account with no clients gets the empty state). No admin gate.
                element: <RequireSession />,
                children: [
                    {
                        path: '',
                        element: <DeveloperPortal />,
                        handle: {
                            layout: 'workspace',
                        } satisfies AppRouteHandle,
                        // Element-less child: a URL and a handle for the open
                        // client. The portal renders the list and the client
                        // itself, so the list never unmounts (workspace.md 1.1).
                        children: [
                            {
                                path: ':clientId',
                                handle: {
                                    pane: 'client',
                                } satisfies AppRouteHandle,
                                // An Active or Deprecated client's tabs
                                // (portal-client-tabs); Settings is the
                                // client's own URL. Element-less too.
                                children: [
                                    {
                                        path: 'credentials',
                                        handle: {
                                            pane: 'credentials',
                                        } satisfies AppRouteHandle,
                                    },
                                    {
                                        path: 'terms',
                                        handle: {
                                            pane: 'terms',
                                        } satisfies AppRouteHandle,
                                    },
                                    {
                                        path: 'usage',
                                        handle: {
                                            pane: 'usage',
                                        } satisfies AppRouteHandle,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            { path: '*', element: <NotFoundPage /> },
        ],
    },
];
